import React, { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { labelsColors, Rect, RectType } from "../../models/Rect";
import { Dialog } from "../dialog/Dialog";
import { ImageCanvas } from "./ImageCanvas";
import { Box, FormControlLabel, Grid, Switch, TextField, Typography } from "@mui/material";
import { translationService } from "../../services/translation/TranslationService";
import { videoPlayerService } from "../../services/video-player/VideoPlayerService";
import dateService from "../../services/date-service/DateService";
import LabelingInfo from "./LabelingInfo";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import dayjs, { Dayjs } from "dayjs";
import { OcrService } from "../../services/ocr/OcrService";

type Props = {
  isVisible: boolean;
  rectTypes: RectType[];
  onClose: () => void;
  onComplete: (setMainInfo: boolean) => void;
  screenshot: string | null;
  rects: Map<RectType, Rect>;
  setRects: (rects: Map<RectType, Rect>) => void;
  ocrService: OcrService;
};

export const LabelingDialog: React.FC<Props> = observer(
  ({ isVisible, rectTypes, onClose, onComplete, screenshot, rects, setRects, ocrService }) => {
    const [rectsInput, setRectsInput] = useState<Map<RectType, string>>(new Map());
    const [rectType, setRectType] = useState<RectType | null>(null);
    const [detectMainInfo, setDetectMainInfo] = useState(true);
    const detectionTime = dateService.formatTime(videoPlayerService.lastScreenshotTimestamp);

    const maxTime: number = (videoPlayerService.videoDuration);
    const startOfDay = dayjs().startOf('day');
    const defaultEndTime = startOfDay.add(maxTime, 'second');
    const [endTimeValue, setEndTimeValue] = useState<Dayjs | null>(null);
    
    const isDialogOpened = useRef(false);
    useEffect(() => {
      if (isVisible && !isDialogOpened.current) {
        const timeToUse = ocrService.userEndTime || maxTime;
        const endTime = startOfDay.add(timeToUse, 'second');
        setEndTimeValue(endTime);
        isDialogOpened.current = true;
      } else if (!isVisible && isDialogOpened.current) {
        isDialogOpened.current = false;
      }
    }, [isVisible, maxTime, startOfDay, ocrService.userEndTime]);

    useEffect(() => {
      let rectsInputCopy = new Map(rectsInput);
      rects.forEach((rect,key) => {
        const x = Math.round(rect!.x);
        const y = Math.round(rect!.y);
        const w = Math.round(rect!.w);
        const h = Math.round(rect!.h);
        rectsInputCopy.set(key, `${x},${y},${w},${h}`); 
      });
      rectsInput.forEach((_,key) => {
      if(!rects.has(key)) rectsInputCopy.delete(key); 
    });
      setRectsInput(rectsInputCopy);
    }, [rects]);
    useEffect(() => {
      setRectType(rectTypes[0]);
    }, [rectTypes]);
    const isEndTimeValid = endTimeValue !== null && 
                          endTimeValue.diff(startOfDay, 'second') > 0 && 
                          endTimeValue.diff(startOfDay, 'second') <= maxTime;

    const saveChanges = () => {
      ocrService.setUserEndTime(endTimeValue);
      onComplete(detectMainInfo);
    };

    if (rectTypes.length === 0 || !screenshot) return null;

    return (
      <Dialog
        titleKey={`ocrService.dialogTitle.${rectTypes[0]}`}
        isVisible={isVisible}
        subTextKey={`ocrService.dialogSubText.${rectTypes[0]}`}
        onHide={onClose}
        buttonOk={{
          textKey: "general.ok",
          action: saveChanges,
          disabled: !isEndTimeValid,
        }}
      >
        <Grid container xs={12} spacing={2}>
          <Grid item xs={12}>
            {rectTypes[0] === "main" && (
              <>
                <FormControlLabel
                  control={
                    <Switch
                      checked={detectMainInfo}
                      onChange={(e) => setDetectMainInfo(e.target.checked)}
                    />
                  }
                  label={translationService.translate(
                    `ocrService.dialogContent.${rectTypes[0]}.toggle`
                  )}
                  sx={{ mr: 4 }}
                />
              </>
            )}
          </Grid>
          {rectTypes[0] === "detections" ? (
            <>
              <Grid item xs={6}>
                <TextField
                  variant="outlined"
                  size="small"
                  value={detectionTime}
                  InputProps={{ readOnly: true }}
                    label={translationService.translate(`ocrService.dialogContent.${rectTypes[0]}.time`)}
                />
              </Grid>
              <Grid item xs={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
              <TimePicker
                views={['minutes', 'seconds']}
                format="mm:ss"
                slotProps={{ textField: { size: "small" } }}
                value={endTimeValue}
                onChange={(newValue) => setEndTimeValue(newValue)}
                minTime={startOfDay.add(1, 'second')}
                maxTime={defaultEndTime}
                referenceDate={startOfDay}
                timeSteps={{ minutes: 1, seconds: 1 }}  
                label={translationService.translate(`ocrService.dialogContent.${rectTypes[0]}.endTime`)}
              />
            </LocalizationProvider>
            </Grid>
          </>
        ) : (
            <Grid item xs={12}>
              <TextField
                variant="outlined"
                size="small"
                value={detectionTime}
                InputProps={{ readOnly: true }}
                  label={translationService.translate(`ocrService.dialogContent.${rectTypes[0]}.time`)}
              />
            </Grid>
          )}
{rectTypes.map((rectTypeItem) => (
  <React.Fragment key={rectTypeItem}>
    <Grid item xs={2}>
      <Box
        component="button"
        type="button"
        onClick={() => setRectType(rectTypeItem)}
        sx={{
          width: 32,
          height: 32,
          bgcolor: rectType === rectTypeItem ? labelsColors[rectTypeItem] : "grey.400",
          border: "none",
          borderRadius: 1,
          cursor: "pointer",
          color: "common.white",
        }}
      />
    </Grid>

    <Grid item xs={9}>
      <TextField
        value={rectsInput.get(rectTypeItem) ?? ""}  // ensure string
        variant="outlined"
        size="small"
        sx={{ flex: 1 }}
        label={translationService.translate(`ocrService.dialogContent.${rectTypeItem}.coords`)}
        InputLabelProps={{ shrink: true, style: { color: labelsColors[rectTypeItem] } }}
        inputProps={{ readOnly: true }} // remove if you add onChange
      />
    </Grid>
  </React.Fragment>
))}
          <Grid item xs={1}>
            <LabelingInfo type={rectTypes[0]} />
          </Grid>
          <Grid item xs={12}>
            <ImageCanvas
              rects={rects}
              rectType={rectType}
              setRects={setRects}
              imageSrc={screenshot}
              disabled={!detectMainInfo}
            />
          </Grid>
        </Grid>
      </Dialog>
    );
  }
);
